import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/common/Layout';
import { activoMantenimientoService } from '../services/activoMantenimiento';
import toast from 'react-hot-toast';
import {
    CalendarClock,
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Wrench,
} from 'lucide-react';

const ESTADO_CONFIG = {
    vigente:    { label: 'Vigente',     icon: CheckCircle2,  rowCls: 'bg-green-50 border-green-200', badgeCls: 'bg-green-100 text-green-700' },
    por_vencer: { label: 'Por vencer',  icon: AlertTriangle, rowCls: 'bg-blue-50 border-blue-200',   badgeCls: 'bg-blue-100 text-blue-700' },
    vencido:    { label: 'Vencido',     icon: XCircle,       rowCls: 'bg-red-50 border-red-200',     badgeCls: 'bg-red-100 text-red-700' },
};

const ORDEN_ESTADO = { vencido: 0, por_vencer: 1, vigente: 2 };

// Página independiente de "Solicitudes": aquí se ve el plan de mantenimiento
// preventivo (vencimientos programados por activo), no los tickets reactivos.
const ProgramacionesMantenimientoPage = () => {
    const [items, setItems] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [filtro, setFiltro] = useState('');

    const cargar = async () => {
        setCargando(true);
        try {
            setItems(await activoMantenimientoService.listarProgramaciones());
        } catch {
            toast.error('Error al cargar las programaciones de mantenimiento');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargar(); }, []);

    const itemsOrdenados = useMemo(() => {
        let lista = [...items];
        if (filtro) lista = lista.filter((it) => it.estado === filtro);
        return lista.sort((a, b) => ORDEN_ESTADO[a.estado] - ORDEN_ESTADO[b.estado] || a.dias_restantes - b.dias_restantes);
    }, [items, filtro]);

    const conteos = useMemo(() => {
        const c = { vencido: 0, por_vencer: 0, vigente: 0 };
        items.forEach((it) => { c[it.estado] = (c[it.estado] || 0) + 1; });
        return c;
    }, [items]);

    return (
        <Layout>
            <div className="p-6">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <CalendarClock className="h-7 w-7 text-teal-600" />
                        Programaciones de Mantenimiento
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Plan de mantenimiento preventivo por activo (recargas, calibraciones, revisiones técnicas),
                        separado de las Solicitudes de trabajo.
                    </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => {
                        const Icon = cfg.icon;
                        return (
                            <button
                                key={key}
                                onClick={() => setFiltro(filtro === key ? '' : key)}
                                className={`rounded-xl border p-4 text-center transition-all ${cfg.rowCls} ${filtro === key ? 'ring-2 ring-teal-500' : ''}`}
                            >
                                <Icon className="h-5 w-5 mx-auto mb-1" />
                                <p className="text-xl font-bold text-gray-800">{conteos[key] || 0}</p>
                                <p className="text-xs text-gray-600">{cfg.label}</p>
                            </button>
                        );
                    })}
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-700 mb-4">
                        {filtro ? `Filtrado: ${ESTADO_CONFIG[filtro].label}` : 'Todos'} — {itemsOrdenados.length} programación{itemsOrdenados.length !== 1 ? 'es' : ''}
                    </h2>

                    {cargando && (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600" />
                        </div>
                    )}

                    {!cargando && itemsOrdenados.length === 0 && (
                        <div className="text-center py-14 text-gray-400">
                            <Wrench className="h-14 w-14 mx-auto mb-3 opacity-20" />
                            <p className="text-sm font-medium">No hay programaciones de mantenimiento registradas.</p>
                            <p className="text-xs mt-1">Se registran desde el detalle de cada activo, en "Activos".</p>
                        </div>
                    )}

                    {!cargando && itemsOrdenados.map((item) => {
                        const cfg = ESTADO_CONFIG[item.estado] || ESTADO_CONFIG.vigente;
                        const Icon = cfg.icon;
                        return (
                            <div
                                key={item.id}
                                className={`w-full flex items-center justify-between gap-3 border rounded-lg px-4 py-3 mb-2 ${cfg.rowCls}`}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <Icon className="h-5 w-5 flex-shrink-0" />
                                    <div className="min-w-0">
                                        <p className="text-sm font-bold text-gray-800 truncate">{item.activo_code} — {item.activo_name}</p>
                                        <p className="text-xs text-gray-500 truncate">{item.tipo || 'Sin tipo'}{item.responsable ? ` · ${item.responsable}` : ''}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    <span className="text-xs text-gray-500">
                                        Vence: {new Date(item.fecha_vencimiento + 'T00:00:00').toLocaleDateString('es-PE')}
                                    </span>
                                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.badgeCls}`}>
                                        {cfg.label}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Layout>
    );
};

export default ProgramacionesMantenimientoPage;
